import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useSessionStore } from '@/apps/session/stores/session'
import { getAppsForMenuWithFeatures, shellApps, type App } from '@/layouts/shell'
import { useCan } from '../../organizations/composables/useCan'

/** The bottom bar's app slots. The fifth is Plus, never an app (docs/plans/mobile-navigation.md MN3). */
export const PHONE_SLOTS = 4

/**
 * The apps the caller may open from the navigation, in `shellApps` order: all of them on the
 * rail; on a phone the first `PHONE_SLOTS` in the bottom bar and the rest in Plus's sheet, so
 * none is out of reach whatever app is added next. A `disabled` app takes no slot.
 */
export function useMenuApps() {
  const route = useRoute()
  const session = useSessionStore()
  const { can } = useCan()

  const menuApps = computed(() =>
    getAppsForMenuWithFeatures(shellApps, session.init?.features ?? [])
      .filter(app => !app.requiredPermission || can(app.requiredPermission))
      .filter(app => !app.requiredOrgFeature || session.hasOrgFeature(app.requiredOrgFeature)),
  )

  const slotted = computed(() => menuApps.value.filter(app => !app.disabled))
  const phoneApps = computed(() => slotted.value.slice(0, PHONE_SLOTS))
  const overflowApps = computed(() => slotted.value.slice(PHONE_SLOTS))

  function isActiveApp(app: App): boolean {
    const currentRouteName = route.name as string
    if (!currentRouteName || !app.route) return false
    return currentRouteName.startsWith(app.route)
  }

  /** The app the route belongs to sits in Plus: Plus is then the bar's active item. */
  const overflowActive = computed(() => overflowApps.value.some(isActiveApp))

  return { menuApps, phoneApps, overflowApps, overflowActive, isActiveApp }
}
